import { useEffect, useState } from "react";
import { API, Storage, Auth } from "aws-amplify";
import { useQuery, gql, useApolloClient } from "@apollo/client";
import {
  Container,
  Stack,
  Box,
  Typography,
  LinearProgress,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import groupArrayofObjectsByKeyAndGenerateArr from "../../../lib/groupArrayofObjectsByKeyAndGenerateArr";
import SimpleTable from "../../../component/SimpleTable";
import { invoiceByBatch } from "./query";
import FileZone from "../../../component/FileZone";
import updateInvoice from "../../../modules/entityModules/billing/onUpdate";

const headers = [
  "number",
  "itemNumber",
  // "mfr",
  // "mpn",
  "unit",
  "desc",
  "code",
  // "poNo",
  "qty",
  "price",
  // "currency",
  "amount",
  // 'subTotal',
];

export default function VATProcess() {
  const navigate = useNavigate();
  const [vatsByNumber, setVatsByNumber] = useState();

  const { entityID } = useParams();
  const client = useApolloClient();
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [hasSubmittedUpdate, setHasSubmittedUpdate] = useState(false);
  const [isLoadingCreate, setIsLoadingCreate] = useState(false);
  const [hasSubmittedCreate, setHasSubmittedCreate] = useState(false);

  let [searchParams, setSearchParams] = useSearchParams();

  const data = searchParams.get("data");

  let dataObj = {};
  if (data) {
    dataObj = JSON.parse(Buffer.from(data, "base64"));
  }

  const batch = dataObj.batch;

  const { data: invoiceByBatchData } = useQuery(gql(invoiceByBatch), {
    variables: { batch, limit: 1000 }, // Set high limit to get all items
    skip: !batch,
  });

  const invoices = invoiceByBatchData?.invoiceByBatch?.items;

  // Log for debugging
  useEffect(() => {
    if (invoices) {
      console.log(
        `[VATProcess] Loaded ${invoices.length} invoice items from batch: ${batch}`,
      );
    }
  }, [invoices, batch]);

  useEffect(() => {
    const items = invoices?.map((x) => {
      return {
        // ...x,
        id: x.id,
        number: x.number,
        itemNumber: x.itemNumber,
        mfr: x.po?.material?.mfr,
        mpn: x.po?.material?.mpn,
        unit: x.po?.material?.unit,
        desc: x.po?.material?.desc,
        code: x.po?.materialEntityRelation?.code,
        poNo: x.po?.no,
        price: x.price,
        currency: x.currency,
        qty: x.qty,
        amount: x.amount,
      };
    });

    if (!items?.length) {
      setVatsByNumber([]);
      return;
    }

    // First group by invoice number
    const groupedByNumber = groupArrayofObjectsByKeyAndGenerateArr(
      items,
      "number",
    );

    // Merge duplicates for display, but keep original items for updates and distribution table
    const vatsByNumber = groupedByNumber.map(
      ({ number, items: invoiceItems }) => {
        // Keep all original items (for updating invoice numbers and distribution table)
        const originalItems = invoiceItems.map((item) => ({ ...item }));

        // Create merged items for display (same code + mpn + price)
        const mergedMap = new Map();
        for (const item of invoiceItems) {
          const key = `${item.code || ""}_${item.mpn || ""}_${item.price}`;
          if (mergedMap.has(key)) {
            const existing = mergedMap.get(key);
            existing.qty = Number(existing.qty) + Number(item.qty);
            existing.amount = Number(existing.amount) + Number(item.amount);
          } else {
            mergedMap.set(key, {
              ...item,
              qty: Number(item.qty),
              amount: Number(item.amount),
            });
          }
        }
        const mergedItems = Array.from(mergedMap.values());
        // Reassign item numbers after merge for display
        mergedItems.forEach((item, idx) => {
          item.itemNumber = idx + 1;
        });

        console.log(
          `[VATProcess] Invoice ${number}: ${originalItems.length} original items, ${mergedItems.length} merged items for display`,
        );
        return { number, items: mergedItems, originalItems };
      },
    );

    const totalOriginalItems = vatsByNumber.reduce(
      (sum, group) => sum + (group.originalItems?.length || group.items.length),
      0,
    );
    const totalMergedItems = vatsByNumber.reduce(
      (sum, group) => sum + group.items.length,
      0,
    );
    console.log(
      `[VATProcess] Total: ${totalOriginalItems} original items, ${totalMergedItems} merged items for display`,
    );
    setVatsByNumber(vatsByNumber);
  }, [invoices]);

  return (
    <Container maxWidth={false}>
      <Stack spacing={5}>
        {vatsByNumber?.length > 0 && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Typography variant="h4">NEW VATS</Typography>
            </Stack>

            {vatsByNumber?.map(({ number, items, originalItems }) => {
              return (
                // <Stack key={number} spacing={2}>
                <VATByNumber
                  key={number}
                  number={number}
                  items={items}
                  originalItems={originalItems}
                />
                // </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

function VATByNumber({ number, items, originalItems }) {
  const client = useApolloClient();

  async function cb(number, files) {
    if (number?.length < 1) {
      return alert("please enter a number first");
    }
    // Update ALL original items (not just merged display items) to the new invoice number
    // This ensures all items in the distribution table get updated
    const itemsToUpdate = originalItems || items;
    console.log(
      `[VATByNumber] Updating ${itemsToUpdate.length} original invoice items to number: ${number}`,
    );
    await Promise.all(
      itemsToUpdate.map(async (x) => {
        const values = { id: x.id, number };
        await updateInvoice({ values, client });
      }),
    );
    console.log(
      `[VATByNumber] Successfully updated ${itemsToUpdate.length} invoice items`,
    );

    await API.post("iSupplyAPI", "/restricted", {
      body: {
        operation: "SEND_VAT_TO_CTM",
        data: {
          meta: {
            invoiceNumber: number,
            // to: "pgchinaeinvoice.im@pg.com",
            // bcc: "steven.lu@hankeno.com",
            isPreview: false,
          },
        },
      },
    });
  }

  // console.log({ number });
  // debugger
  return (
    <Stack spacing={2} justifyContent="center">
      <SimpleTable headers={headers} rows={items} />
      {/* <Stack direction="row" spacing={2} justifyContent="center"> */}
      {/* <LoadingButton variant='contained'> submit</LoadingButton> */}
      <Box sx={{ alignSelf: "center" }}>
        <FileZone
          storagePath={number}
          allowInputNumber
          cb={cb}
          maxUploadFiles={1}
        />
      </Box>
      {/* </Stack> */}
    </Stack>
  );
}
